import React, { useState, useEffect, useRef } from 'react'
import { Stack, TextField } from '@fluentui/react'
import { SendRegular, MicRegular } from '@fluentui/react-icons'

import Send from '../../assets/Send.svg'

import styles from './QuestionInput.module.css'
import { ChatMessage } from '../../api'
import { useSpeechRecognition } from '../../hooks/useSpeechRecognition'

interface Props {
  onSend: (question: ChatMessage['content'], id?: string) => void
  disabled: boolean
  placeholder?: string
  clearOnSend?: boolean
  conversationId?: string
}

// Define the types for the props
interface QuestionInputProps {
  onSend: (question: string, conversationId?: string) => void; // Define the onSend function type
  disabled?: boolean; // Optional prop
  placeholder?: string; // Optional prop
  clearOnSend?: boolean; // Optional prop
  conversationId?: string; // Optional prop
  value: string; // Required string prop
  onChange: (newValue: string) => void; // Define the onChange function type
}

export const QuestionInput: React.FC<QuestionInputProps> = ({
  onSend,
  disabled,
  placeholder,
  clearOnSend,
  conversationId,
  value,
  onChange
}) => {
  const [question, setQuestion] = useState<string>(value || '')

  // speech recognition integration
  const baseSpeechTextRef = useRef<string>('')
  const { supported, listening, start, stop } = useSpeechRecognition({
    interimResults: true,
    continuous: false,
    onResult: ({ transcript, isFinal }) => {
      const base = baseSpeechTextRef.current || ''
      const newText = (base + transcript)
      setQuestion(newText)
      onChange(newText)

      // Auto-submit when speech recognition completes
      if (isFinal && newText.trim()) {
        // Small delay to ensure state is updated and user can see the final text
        setTimeout(() => {
          if (conversationId) {
            onSend(newText, conversationId)
          } else {
            onSend(newText)
          }

          if (clearOnSend) {
            setQuestion('')
            onChange('')
          }
        }, 300)
      }
    },
  })


  const toggleMic = () => {
    if (disabled) return
    if (!supported) return
    if (listening) {
      stop()
    } else {
      baseSpeechTextRef.current = question ? question + ' ' : ''
      start()
    }
  }

  // Use useEffect to sync internal state with the prop value
  useEffect(() => {
    setQuestion(value);
  }, [value]);

  const sendQuestion = () => {
    if (disabled || !question.trim()) {
      return
    }

    // If voice input is active, stop it before sending
    if (listening) {
      stop()
    }

    const questionTest: ChatMessage["content"] = question.toString();

    if (conversationId && questionTest !== undefined) {
      onSend(questionTest, conversationId)
    } else {
      onSend(questionTest)
    }

    if (clearOnSend) {
      setQuestion('')
    }
  }

  const onEnterPress = (ev: React.KeyboardEvent<Element>) => {
    if (ev.key === 'Enter' && !ev.shiftKey && !(ev.nativeEvent?.isComposing === true)) {
      ev.preventDefault()
      sendQuestion()
    }
  }

  const onQuestionChange = (_ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
    setQuestion(newValue || '')
    onChange(newValue || '') // Call onChange prop to update parent state
  }

  const sendQuestionDisabled = disabled || !question.trim()

  return (
    <Stack className={styles.questionInputContainer}>
      <Stack className={styles.questionInputInnerContainer}>
        <TextField
          className={styles.questionInputTextArea}
          placeholder={placeholder}
          multiline
          resizable={false}
          borderless
          value={question}
          onChange={onQuestionChange}
          onKeyDown={onEnterPress}
        />
        <Stack className={styles.questionInputRightItemsContainer}>
          <div
            className={styles.micButtonContainer}
            role="button"
            tabIndex={supported && !disabled ? 0 : -1}
            aria-label={supported ? (listening ? 'Stop voice input' : 'Start voice input') : 'Voice input not supported'}
            aria-pressed={listening}
            aria-disabled={disabled || !supported}
            title={!supported ? 'Voice input is not supported in this browser' : (listening ? 'Listening… click to stop' : 'Click to speak your question')}
            onClick={() => { if (!disabled && supported) toggleMic() }}
            onKeyDown={e => (e.key === 'Enter' || e.key === ' ' ? (!disabled && supported ? toggleMic() : null) : null)}>
            <MicRegular className={!supported || disabled ? styles.micIconDisabled : (listening ? styles.micIconActive : styles.micIcon)} />
          </div>
          <div
            className={styles.questionInputSendButtonContainer}
            role="button"
            tabIndex={0}
            aria-label="Ask question button"
            onClick={sendQuestion}
            onKeyDown={e => (e.key === 'Enter' || e.key === ' ' ? sendQuestion() : null)}>
            {sendQuestionDisabled ? (
              <SendRegular className={styles.questionInputSendButtonDisabled} />
            ) : (
              <img src={Send} className={styles.questionInputSendButton} alt="Send Button" />
            )}
          </div>
        </Stack>
      </Stack>
    </Stack>
  )
}
